import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { heroImages, companyInfo } from '../../data/tourData';
import SearchModal from '../common/SearchModal';
import './HeroSection.css';

const HeroSection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  // Auto-advance background images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Hide loading after first image loads
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setIsSearchModalOpen(true);
    }
  };

  const handleExploreTrip = () => {
    setIsSearchModalOpen(true);
  };

  const heroDestinations = [
    { name: 'Spiti Valley', link: '/spiti-valley' },
    { name: 'Meghalaya', link: '/meghalaya' },
    { name: 'Kashmir', link: '/kashmir' },
    { name: 'Kerala', link: '/kerala' },
    { name: 'Leh Ladakh', link: '/leh-ladakh' }
  ];

  return (
    <section className="hero">
      {/* Background Image Slider */}
      <div className="hero-background-slider">
        {isLoading && (
          <div className="hero-loading">
            <div className="loading-spinner"></div>
            <p>Loading amazing destinations...</p>
          </div>
        )}
        
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImageIndex}
            className="background-slide"
            style={{
              backgroundImage: `url('${heroImages[currentImageIndex].highQuality}')`
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          />
        </AnimatePresence>
      </div>

      {/* Hero Content */}
      <div className="hero-content">
        <motion.h1 
          className="hero-title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Book Your Trip to
        </motion.h1>
        
        <motion.div 
          className="hero-destinations"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {heroDestinations.map((destination, index) => (
            <motion.span 
              key={destination.name}
              className="destination"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
            >
              <Link to={destination.link}>{destination.name}</Link>
            </motion.span>
          ))}
        </motion.div>
        
        <motion.p 
          className="hero-subtitle"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          Wander | Travel | Connect | Repeat
        </motion.p>
        
        <motion.form 
          className="hero-search"
          onSubmit={handleSearch}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <input
            type="text"
            placeholder="Search destinations..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className="search-btn" onClick={handleExploreTrip}>
            <FaSearch />
            <span>Explore Trip</span>
          </button>
        </motion.form>
      </div>

      {/* Hero Stats */}
      <motion.div 
        className="hero-stats"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
      >
        {companyInfo.stats.map((stat, index) => (
          <motion.div 
            key={index}
            className="stat-item"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 1.4 + index * 0.1 }}
          >
            <h3>{stat.label}</h3>
            <p>{stat.value}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Search Modal */}
      <SearchModal 
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        initialQuery={searchQuery}
      />
    </section>
  );
};

export default HeroSection;
